import React, {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ShareableTable } from './data';
import { DataRoomShareableLink } from 'models/ShareableLink';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import usePastTime from 'hooks/usePastTime';
import { Button } from 'ncoded-component-library';
import SettingsIcon from 'icons/Settings.icon';
import {
  getIsAccessAddPasword,
  getIsAccessAnyone,
} from 'router/subrouters/DataRoom/utils';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ResolvePopup from 'components/ResolvePopup';
import EditShareableLinkContext from 'router/subrouters/DataRoom/providers/EditShareableLink/EditShareableLink.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import ClipboardcopyIcon from 'icons/Clipboardcopy.icon';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import {
  getResolveUrl,
  getShareableLinkRoute,
  isShareableLinkNdaProcessing,
  dateFormat,
} from 'utils';
import TrashIcon from 'icons/Trash.icon';
import RefreshIcon from 'icons/Refresh.icon';
import {
  SL_NDA_COPY,
  SL_NDA_STATUS,
} from 'router/subrouters/DataRoom/models/DataRoomNDA';
import { NDATemplate } from 'models/Nda';
import NdaListModal from 'components/NdaListModal';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import ProlongShareableLinkModal from '../../ProlongShareableLinkModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import CalendarIcon from 'icons/Calendar.icon';

const MAX_NUM_OF_PEOPLE = 2;

type CellProps = {
  link: DataRoomShareableLink;
  updateShareableLink?: (link: DataRoomShareableLink) => void;
};

const NameCell: FC<CellProps> = ({ link }) => {
  const { name } = link;

  return <div className="name-cell">{name}</div>;
};

export const NumberAcessCell: FC<CellProps> = ({ link }) => {
  const {
    accessNumber,
    linkAccess,
    anyoneWithNonPublicDomain,
    emailDomain,
    recipientEmailAddresses,
  } = link;

  const { t } = useTranslation();

  const isAccessAnyone = getIsAccessAnyone(linkAccess);
  const isAccessAnyoneWithPassword = getIsAccessAddPasword(linkAccess);

  const peopleWithAccess = useMemo(() => {
    if (accessNumber > MAX_NUM_OF_PEOPLE)
      return (
        <ResolvePopup
          onHover
          contentClassName="number-cell__popup"
          portalClassName="number-cell__portal"
          popupContent={
            <ul>
              {recipientEmailAddresses.map((member) => (
                <li key={member}>{member}</li>
              ))}
            </ul>
          }
        >
          {t('numberOfPeople', { count: accessNumber })}
        </ResolvePopup>
      );

    return recipientEmailAddresses?.join(', ');
  }, [accessNumber, recipientEmailAddresses, t]);

  return (
    <div className="number-access-cell">
      {isAccessAnyone ? (
        t('anyoneWithLink')
      ) : isAccessAnyoneWithPassword ? (
        t('anyoneWithPassword')
      ) : anyoneWithNonPublicDomain ? (
        <>
          {t('anyoneWithDomain')} <b>{emailDomain}</b>
        </>
      ) : (
        peopleWithAccess
      )}
    </div>
  );
};

const FilesCell: FC<CellProps> = ({ link }) => {
  const { fileIds } = link;

  return <div className="files-cell">{fileIds.length}</div>;
};

const LastAccessCell: FC<CellProps> = ({ link }) => {
  const { lastAccessDate } = link;

  const pastTime = usePastTime();

  const { t } = useTranslation();

  return (
    <div className="last-access-cell">
      {lastAccessDate ? pastTime(lastAccessDate) : t('nobodyAccessed')}
    </div>
  );
};

export const CreatedCell: FC<CellProps> = ({ link }) => {
  const { createdAt } = link;

  const pastTime = usePastTime();

  return <div className="created-cell">{pastTime(createdAt)}</div>;
};

export const ExpiresOnCell: FC<CellProps> = ({ link }) => {
  const { linkExpiresDate } = link;

  if (!linkExpiresDate) return null;

  return <div className="expires-cell">{dateFormat(linkExpiresDate)}</div>;
};

export const ActiveCell: FC<CellProps> = ({ link, updateShareableLink }) => {
  const { _id, isActive, linkExpiresDate } = link;

  const [isLoading, setIsLoading] = useState(false);

  const { doesntHaveAccess } = useContext(DataRoomContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const isButtonsDisabled = useMemo(
    () => isLoading || doesntHaveAccess,
    [doesntHaveAccess, isLoading],
  );

  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  const { t } = useTranslation();

  const isActiveLink = useMemo(() => {
    const now = +new Date();
    const expiresDate = +new Date(linkExpiresDate);

    return isActive && now < expiresDate;
  }, [isActive, linkExpiresDate]);

  const changeState = useCallback(async () => {
    if (doesntHaveAccess) return;

    try {
      setIsLoading(true);

      const { data } =
        await dataRoomApi.shareableLinks.changeShareableLinkState(
          _id,
          !isActiveLink,
        );

      updateShareableLink?.({ ...link, ...data });
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    _id,
    isActiveLink,
    doesntHaveAccess,
    link,
    updateShareableLink,
    popServerError,
  ]);

  const onSuccessProlong = useCallback(
    async (patchedValues: Partial<DataRoomShareableLink>) => {
      updateShareableLink?.({
        ...link,
        ...patchedValues,
      });
    },
    [link, updateShareableLink],
  );

  return (
    <div className="active-cell" onClick={(e) => e.stopPropagation()}>
      <Button
        variant="link"
        className={classNames({ alert: isActiveLink })}
        disabled={isButtonsDisabled}
        icon={isActiveLink ? TrashIcon : RefreshIcon}
        onClick={changeState}
      >
        {t(isActiveLink ? 'revoke' : 'renew')}
      </Button>

      {isActiveLink && (
        <>
          <Button
            variant="link"
            disabled={isButtonsDisabled}
            onClick={modal?.open}
            icon={CalendarIcon}
          >
            {t('prolong')}
          </Button>
          <ProlongShareableLinkModal
            ref={modalRef}
            shareableLinkId={_id}
            linkExpiresDate={linkExpiresDate}
            onSuccess={onSuccessProlong}
          />
        </>
      )}
    </div>
  );
};

export const CopyCell: FC<CellProps> = ({ link }) => {
  const { _id } = link;

  const copyToClipboard = useCopyToClipboard();

  return (
    <Button
      className="copy-cell"
      variant="icon"
      icon={ClipboardcopyIcon}
      onClick={(e) =>
        copyToClipboard(
          e,
          getResolveUrl(getShareableLinkRoute(_id, 'documents')),
        )
      }
    />
  );
};

export const LinkManageCell: FC<CellProps> = ({ link }) => {
  const { doesntHaveAccess } = useContext(DataRoomContext);
  const { openEditShareableLink } = useContext(EditShareableLinkContext);

  return (
    <Button
      className="manage-cell"
      variant="icon"
      disabled={doesntHaveAccess}
      icon={SettingsIcon}
      onClick={(e) => {
        e.stopPropagation();

        if (doesntHaveAccess) return;
        openEditShareableLink(link);
      }}
    />
  );
};

export const ActionsCell: FC<CellProps> = ({ link, updateShareableLink }) => {
  return (
    <div className="actions-cell">
      <ActiveCell link={link} updateShareableLink={updateShareableLink} />
      <CopyCell link={link} />
      <LinkManageCell link={link} />
    </div>
  );
};

const NdaCell: FC<CellProps> = ({ link, updateShareableLink }) => {
  const { dataRoomNdaId, dataRoomNda } = link;

  const { status, envelopeTemplateId } = dataRoomNda || {};

  const modalRef = useRef<OverlayRef>();

  const { isSuperAdmin } = useContext(CurrentUserContext);

  const { t } = useTranslation();

  const isProcessing = isShareableLinkNdaProcessing(status);

  const canConnectNda = isSuperAdmin && !envelopeTemplateId && isProcessing;

  const classes = classNames('nda-cell', status);

  const onUploadNda = useCallback(
    async (template: NDATemplate) => {
      await dataRoomApi.nda.linkEnvelopeTemplate(
        dataRoomNdaId,
        template.templateId,
        template.name,
      );

      updateShareableLink({
        ...link,
        dataRoomNdaId: template.templateId,
        dataRoomNda: {
          ...dataRoomNda,
          status: SL_NDA_STATUS.ACTIVE,
          envelopeTemplateId: template.templateId,
        },
      });
    },
    [dataRoomNdaId, dataRoomNda, link, updateShareableLink],
  );

  if (!status) return <div className={classes}>{t('noNda')}</div>;

  return (
    <>
      <div className={classes}>
        {t(SL_NDA_COPY[status])}

        {canConnectNda && (
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              modalRef?.current.open();
            }}
          >
            {t('connectNda')}
          </Button>
        )}
      </div>
      {canConnectNda && (
        <div onClick={(e) => e.stopPropagation()}>
          <NdaListModal
            ref={modalRef}
            description={t('connectNondisclosureAgrementWithSecureLink')}
            onSelectNda={onUploadNda}
          />
        </div>
      )}
    </>
  );
};

export const ProtectionCell: FC<CellProps> = ({
  link,
  updateShareableLink,
}) => {
  const {
    includeNdaProtection,
    dataRoomNda,
    includeTermsAndConditionProtection,
  } = link;

  const { t } = useTranslation();

  if (includeNdaProtection && dataRoomNda)
    return <NdaCell link={link} updateShareableLink={updateShareableLink} />;

  return (
    <div className="protection-cell">
      {t(includeTermsAndConditionProtection ? 'tAndC' : 'noNDA')}
    </div>
  );
};

const AccessCell: FC<CellProps> = (props) => (
  <div className="link-access-cell">
    <NumberAcessCell {...props} />
    {' + '}
    <ProtectionCell {...props} />
  </div>
);

export const BODY_CONTENT_GENERATE: Partial<
  Record<keyof ShareableTable, (props: CellProps) => ReactNode>
> = {
  name: (props) => <NameCell {...props} />,
  accessNumber: (props) => <AccessCell {...props} />,
  fileIds: (props) => <FilesCell {...props} />,
  lastAccessDate: (props) => <LastAccessCell {...props} />,
  createdAt: (props) => <CreatedCell {...props} />,
  linkExpiresDate: (props) => <ExpiresOnCell {...props} />,
  linkAccess: (props) => <ActionsCell {...props} />,
};
